/**
 * Toast Store
 *
 * Non-blocking status messages for the portal. Replaces the native alert()
 * calls that froze the whole page behind a browser dialog the user had to
 * dismiss before they could do anything else.
 *
 * Components read `toasts` through the store; everything else should call
 * showToast with its tone, or the showErrorToast / showInfoToast helpers,
 * which work from outside React (event handlers, services) just as well as
 * inside it.
 */

import { create } from 'zustand';
import { forgetAtSessionEnd } from '../../lib/session-end';

export type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  title: string;
  /** Optional detail line, e.g. the reason an action failed. */
  message?: string;
}

/** Errors linger: they usually carry a reason the user needs time to read. */
const DURATION_BY_TYPE: Record<ToastType, number> = {
  success: 4000,
  info: 5000,
  error: 8000,
};

interface ToastState {
  toasts: Toast[];
  dismiss: (id: number) => void;
}

let nextToastId = 1;

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/**
 * Queue a toast. Returns its id so a caller can dismiss it early.
 */
export function showToast(type: ToastType, title: string, message?: string): number {
  const id = nextToastId++;
  useToastStore.setState((state) => ({ toasts: [...state.toasts, { id, type, title, message }] }));

  window.setTimeout(() => {
    useToastStore.getState().dismiss(id);
  }, DURATION_BY_TYPE[type]);

  return id;
}

// Every toast gone at once as a session ends, so the last one's words do not stand over the next sign-in.
forgetAtSessionEnd(() => useToastStore.setState({ toasts: [] }));

export const showErrorToast = (title: string, message?: string) => showToast('error', title, message);
export const showInfoToast = (title: string, message?: string) => showToast('info', title, message);
